import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faShieldAlt, faPen, faKey } from '@fortawesome/free-solid-svg-icons';

import { Button, Spinner, Card } from '@/components/ui';
import { Input } from '@/components/forms';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import { ROUTES } from '@/constants/routes';
import { sanitizeUserProfile } from '@/lib/sanitize';
import { ChangePasswordModal } from '@/pages/auth/ChangePassword/ChangePassword';
import type { UserDetails } from '@/types/auth';
import styles from './Profile.module.css';

export function Profile() {
  const navigate = useNavigate();
  const { logOut, getUser } = useAuthStore();
  const { openModal } = useModalStore();
  const [userDetails, setUserDetails] = useState<UserDetails | undefined>(
    () => getUser() ?? undefined,
  );
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    setUserDetails(getUser() ?? undefined);
  }, [getUser]);

  // Sanitize user data before display
  const sanitizedProfile = useMemo(() => {
    if (!userDetails) return null;
    return sanitizeUserProfile(userDetails);
  }, [userDetails]);

  const handleLogout = async () => {
    setLoggingOut(true);
    await logOut();
    navigate('/');
  };

  const handleChangePassword = () => {
    openModal(<ChangePasswordModal />);
  };

  const handleSetup2FA = () => {
    toast.error('Coming Soon');
  };

  const handleEditProfile = () => {
    toast.error('Coming Soon');
  };

  if (!userDetails || !sanitizedProfile) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>Profile data could not be loaded</p>
          <Button onClick={() => navigate(ROUTES.login)}>Go to login</Button>
        </div>
      </div>
    );
  }

  const displayName = sanitizedProfile.username || sanitizedProfile.email?.split('@')[0] || 'User';
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <div className={styles.container}>
      <div className={styles.sections}>
        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2>Personal Information</h2>
            <span className={styles.roleBadge}>Administrator</span>
          </div>

          <div className={styles.avatarSection}>
            <div className={styles.avatar}>{initials}</div>
            <div className={styles.avatarInfo}>
              <h3>Profile Picture</h3>
              <p>Your avatar is automatically generated from your initial.</p>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.formGrid}>
            <Input
              name="username"
              label="Username"
              value={sanitizedProfile.username || ''}
              setValue={() => {}}
              placeholder="Username"
              isReadOnly
            />
            <Input
              name="email"
              label="Email Address"
              value={sanitizedProfile.email || ''}
              setValue={() => {}}
              placeholder="user@example.com"
              isReadOnly
            />
            <Input
              name="firstName"
              label="First Name"
              value={sanitizedProfile.first_name || ''}
              setValue={() => {}}
              placeholder="Your first name"
              isReadOnly
            />
            <Input
              name="lastName"
              label="Last Name"
              value={sanitizedProfile.last_name || ''}
              setValue={() => {}}
              placeholder="Your last name"
              isReadOnly
            />
          </div>

          <div className={styles.cardActions}>
            <Button variant="primary" size="md" onClick={handleEditProfile}>
              <FontAwesomeIcon icon={faPen} /> Modify Data
            </Button>
          </div>
        </Card>

        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2>Security</h2>
          </div>

          <div className={styles.settingsList}>
            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingIcon}>
                  <FontAwesomeIcon icon={faKey} />
                </div>
                <div>
                  <h4>Password</h4>
                  <p>Protect access to your account with a secure password</p>
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={handleChangePassword}>
                Change
              </Button>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingIcon}>
                  <FontAwesomeIcon icon={faShieldAlt} />
                </div>
                <div>
                  <h4>Two-Step Authentication (2FA)</h4>
                  <p>Add an extra layer of security</p>
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={handleSetup2FA}>
                Set Up
              </Button>
            </div>
          </div>
        </Card>

        <div className={styles.dangerZone}>
          <Button variant="danger" size="md" onClick={handleLogout} disabled={loggingOut}>
            {loggingOut ? (
              <Spinner size="sm" variant="primary" />
            ) : (
              <>
                <FontAwesomeIcon icon={faSignOutAlt} /> Log Out
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
